<template>
  <v-app :class="isDark ? 'theme--dark' : 'theme--light'">
    <div class="auth-page-premium">
      <div class="auth-bg-gradient"></div>

      <!-- Animated blobs -->
      <div class="auth-blob blob-1"></div>
      <div class="auth-blob blob-2"></div>

      <!-- Card -->
      <div class="auth-card-premium">
        <!-- Logo -->
        <div class="mb-5 d-flex justify-center">
          <v-img src="/LogoSvg.svg" contain height="70" max-width="220"></v-img>
        </div>
        <div class="auth-title-premium mt-6">Verify OTP</div>
        <div class="auth-sub-premium">
          Enter the OTP sent to your mobile number
        </div>

        <!-- Mobile (readonly) -->
        <div class="otp-mobile-badge">
          <v-icon small :color="themeColor">mdi-cellphone</v-icon>
          <span class="otp-mobile-num">{{ user.mobileNo }}</span>
          <router-link
            to="/societyadmin/login"
            style="font-size: 11px; text-decoration: none"
            :style="`color:${themeColor}`"
            >Change</router-link
          >
        </div>

        <!-- OTP field -->
        <div class="otp-container mt-6 mb-4">
          <div class="otp-inputs">
            <input
              v-for="(digit, index) in 6"
              :key="index"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              maxlength="1"
              class="otp-box"
              :value="otpArray[index]"
              @input="onOtpInput(index, $event)"
              @keydown="onOtpKeydown(index, $event)"
              @paste="onOtpPaste"
              ref="otpBoxes"
              autocomplete="one-time-code"
            />
          </div>
          <!-- Hidden input for vee-validate -->
          <input 
            type="hidden" 
            v-model="user.otp" 
            name="otp" 
            data-vv-as="OTP"
            v-validate="'required|length:6'" 
          />
          <div v-if="errors.first('otp') || serverSideErrors.otp" class="error-msg mt-2">
            {{ errors.first('otp') || serverSideErrors.otp }}
          </div>
        </div>

        <!-- Verify Button -->
        <v-btn
          block
          depressed
          large
          :loading="loading"
          @click="login"
          class="rounded-lg mt-2"
          :style="`background: linear-gradient(135deg, ${themeColor}, ${darkenColor(
            themeColor,
          )}); color:#fff; font-size:14px; font-weight:700; letter-spacing:0; text-transform:none; height:48px;`"
        >
          Verify OTP
          <v-icon right style="font-size: 16px">mdi-shield-check</v-icon>
        </v-btn>

        <!-- Resend hint -->
        <div class="auth-hint-box mt-4">
          <v-icon small :color="themeColor">mdi-information-outline</v-icon>
          <span
            >Didn't receive the OTP? Please wait 30 seconds and try logging in
            again.</span
          >
        </div>

        <!-- Back to login -->
        <div class="mt-4 text-center">
          <router-link
            to="/societyadmin/login"
            style="font-size: 12.5px; font-weight: 600; text-decoration: none"
            :style="`color:${themeColor}`"
          >
            ← Back to Login
          </router-link>
        </div>
      </div>
    </div>

    <Alert />
  </v-app>
</template>

<script>
import Alert from "../../Alert.vue";
import "@/assets/style.css";
import { mapActions, mapMutations, mapState } from "vuex";

export default {
  components: { Alert },

  data() {
    return {
      showPassword: false,
      user: { mobileNo: "", otp: "" },
      otpArray: ["", "", "", "", "", ""],
      loading: false,
      serverSideErrors: {},
    };
  },

  computed: {
    ...mapState("theme", ["isDark", "primaryColor"]),
    themeColor() {
      return this.isDark ? "#525252" : "#f87676";
    },
  },

  watch: {
    otpArray: {
      deep: true,
      handler(val) {
        this.user.otp = val.join("");
        this.serverSideErrors.otp = null;
      }
    }
  },

  created() {
    this.resetTheme();
    window.__societyVuetify = this.$vuetify;
    this.applyAppearance({ vuetify: this.$vuetify });
    this.initThemeListener();
  },

  methods: {
    ...mapActions("theme", ["applyAppearance", "initThemeListener"]),
    onOtpInput(index, event) {
      const val = event.target.value;
      // Only allow numeric input
      if (val && !/^\d$/.test(val)) {
        this.$set(this.otpArray, index, "");
        event.target.value = "";
        return;
      }
      this.$set(this.otpArray, index, val);
      if (val && index < 5) {
        this.$refs.otpBoxes[index + 1].focus();
      }
    },
    onOtpKeydown(index, event) {
      if (event.key === 'Backspace' && !this.otpArray[index] && index > 0) {
        this.$refs.otpBoxes[index - 1].focus();
      }
    },
    onOtpPaste(event) {
      event.preventDefault();
      const pasteData = (event.clipboardData || window.clipboardData).getData('text');
      if (!pasteData) return;
      const digits = pasteData.replace(/\D/g, '').split('').slice(0, 6);
      digits.forEach((digit, i) => {
        this.$set(this.otpArray, i, digit);
      });
      if (digits.length > 0) {
        const nextIndex = Math.min(digits.length, 5);
        this.$refs.otpBoxes[nextIndex].focus();
      }
    },

    ...mapMutations("alert", ["setAlert"]),
    ...mapMutations("theme", [
      "setPrimaryColor",
      "setButtonColor",
      "resetTheme"
    ]),

    darkenColor(hex) {
      if (!hex || !hex.startsWith("#")) return "#cc5555";
      try {
        let r = parseInt(hex.slice(1, 3), 16);
        let g = parseInt(hex.slice(3, 5), 16);
        let b = parseInt(hex.slice(5, 7), 16);
        return `#${Math.max(0, r - 30)
          .toString(16)
          .padStart(2, "0")}${Math.max(0, g - 30)
          .toString(16)
          .padStart(2, "0")}${Math.max(0, b - 30)
          .toString(16)
          .padStart(2, "0")}`;
      } catch {
        return hex;
      }
    },
    login() {
      if (this.user.otp) {
        this.user.otp = this.user.otp.trim();
      }
      this.$validator.validateAll().then((result) => {
        // Ensure OTP is exactly 6 digits manually if vee-validate ignores hidden input
        if (this.user.otp.length !== 6) {
          this.$set(this.serverSideErrors, 'otp', 'OTP must be exactly 6 digits.');
          return;
        }
        
        if (result) {
          this.loading = true;
          this.$axios
            .post("/societyadmin/verifyOtp", this.user)
            .then((response) => {
              const data = response.data;
              this.loading = false;

              if (data.status) {
                localStorage.setItem("user", JSON.stringify(data.society));

                const isProd = window.location.hostname.includes('ismartsociety.com');

                document.cookie = isProd
                  ? "iss_logged_in=1; path=/; domain=.ismartsociety.com; max-age=86400; SameSite=Lax; Secure"
                  : "iss_logged_in=1; path=/; max-age=86400; SameSite=Lax";
                  
                // Notify web frontend of login (BroadcastChannel + cookie)
                // document.cookie = "iss_logged_in=1; path=/; domain=.ismartsociety.com; max-age=86400; SameSite=Lax; Secure";
                try {
                  const bc = new BroadcastChannel("iss_auth");
                  bc.postMessage("login");
                  bc.close();
                } catch (e) { /* BroadcastChannel not supported */ }

                this.$axios.defaults.headers.common = {
                  Authorization: "Bearer " + data.society.token,
                };

                const pc = data.society.theme_primary_color || "#f87676";
                const bc = data.society.theme_button_color || "#30384d";
                this.setPrimaryColor({ primaryColor: pc });
                this.setButtonColor({ buttonColor: bc });
                this.applyAppearance({ vuetify: this.$vuetify });

                this.setAlert({
                  showAlert: true,
                  variant: "green accent-4",
                  timeout: "2000",
                  message: data.message,
                });
                this.$router.push({ path: "/societyadmin/dashboard/" });
              } else {
                if (data.errors) {
                  this.serverSideErrors = {};
                  for (let i = 0; i < data.errors.length; i++) {
                    this.serverSideErrors[data.errors[i].field] =
                      data.errors[i].message;
                  }
                } else {
                  this.setAlert({
                    showAlert: true,
                    variant: "red accent-3",
                    timeout: "2000",
                    message: data.message,
                  });
                }
              }
            });
        }
      });
    },
  },

  mounted() {
    const mobileNo = this.$route?.params?.mobileNo;
    if (!mobileNo) {
      this.$router.push({ path: "/societyadmin/login" });
      return;
    }
    this.user.mobileNo = mobileNo;
  },
};
</script>

<style scoped>
.auth-page-premium {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: url("/security.svg");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
}

.auth-bg-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(15, 23, 41, 0.7) 0%,
    rgba(15, 23, 41, 0.4) 100%
  );
  backdrop-filter: blur(2px);
  pointer-events: none;
}

/* Blobs - adjusted for consistency */
.auth-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.2;
  animation: blobFloat 12s ease-in-out infinite alternate;
}
.blob-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, var(--brand, #10b981), transparent);
  top: -150px;
  left: -150px;
}
.blob-2 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, #3b82f6, transparent);
  bottom: -100px;
  right: -100px;
  animation-delay: 3s;
}
@keyframes blobFloat {
  from {
    transform: translate(0, 0) scale(1);
  }
  to {
    transform: translate(20px, 20px) scale(1.08);
  }
}

.auth-card-premium {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 24px;
  padding: 48px 40px;
  width: 90%;
  max-width: 420px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  text-align: center;
  position: relative;
  z-index: 10;
  animation: cardIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes cardIn {
  from {
    opacity: 0;
    transform: translateY(24px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.auth-logo-wrap {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  margin: 0 auto 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(248, 118, 118, 0.35);
}

.auth-title-premium {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.04em;
  color: var(--tx, #111827);
  margin-bottom: 6px;
}
.auth-sub-premium {
  font-size: 13px;
  color: var(--tx3, #6b7280);
  margin-bottom: 20px;
}

/* Mobile badge */
.otp-mobile-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--surface2, #f9fafb);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 8px;
  padding: 9px 16px;
  font-size: 13px;
  margin-bottom: 4px;
}
.otp-mobile-num {
  font-family: "JetBrains Mono", monospace;
  font-size: 14px;
  font-weight: 700;
  color: var(--tx, #111827);
  letter-spacing: 0.05em;
}

/* Hint box */
.auth-hint-box {
  background: var(--surface2, #f9fafb);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 8px;
  padding: 11px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--tx3, #6b7280);
  text-align: left;
}

/* Theme toggle */
.theme-toggle-pill {
  width: 56px;
  height: 30px;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 15px;
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 3px 4px;
  transition: all 0.25s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.theme-toggle-pill.dark-mode {
  background: #162035;
  border-color: rgba(255, 255, 255, 0.12);
}
.theme-toggle-thumb {
  width: 22px;
  height: 22px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.theme-toggle-pill.dark-mode .theme-toggle-thumb {
  transform: translateX(26px);
  background: #253350;
}

/* OTP Boxes */
.otp-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.otp-inputs {
  display: flex;
  gap: 12px;
  justify-content: center;
}
.otp-box {
  width: 44px;
  height: 44px;
  border: 2px solid var(--border, #e5e7eb);
  border-radius: 12px;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  color: var(--tx, #111827);
  background: var(--surface, #ffffff);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);
}
.theme--dark .otp-box {
  background: rgba(30, 41, 59, 0.6);
  border-color: rgba(255, 255, 255, 0.1);
  color: #f1f5f9;
}
.otp-box:focus {
  outline: none;
  border-color: var(--brand, #f87676);
  box-shadow: 0 0 0 4px rgba(248, 118, 118, 0.15);
  transform: translateY(-2px);
}
.error-msg {
  color: #ef4444;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  animation: fadeIn 0.3s ease;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
